// ISO countries and US/CA/AU subdivisions, copied from the rep-finder list
// (lib/reps/territories.js) so that branch can merge on its own.
// Country values are alpha-2. Subdivision values are ISO 3166-2 (US-TX).

const COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS", "BT", "BV", "BW", "BY", "BZ",
  "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ",
  "DE", "DJ", "DK", "DM", "DO", "DZ",
  "EC", "EE", "EG", "EH", "ER", "ES", "ET",
  "FI", "FJ", "FK", "FM", "FO", "FR",
  "GA", "GB", "GD", "GE", "GF", "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY",
  "HK", "HM", "HN", "HR", "HT", "HU",
  "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT",
  "JE", "JM", "JO", "JP",
  "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ",
  "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY",
  "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK", "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ",
  "NA", "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ",
  "OM",
  "PA", "PE", "PF", "PG", "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY",
  "QA",
  "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV", "SX", "SY", "SZ",
  "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO", "TR", "TT", "TV", "TW", "TZ",
  "UA", "UG", "UM", "US", "UY", "UZ",
  "VA", "VC", "VE", "VG", "VI", "VN", "VU",
  "WF", "WS",
  "YE", "YT",
  "ZA", "ZM", "ZW",
]

const SUBDIVISIONS = {
  AU: [
    ["ACT", "Australian Capital Territory"],
    ["NSW", "New South Wales"],
    ["NT", "Northern Territory"],
    ["QLD", "Queensland"],
    ["SA", "South Australia"],
    ["TAS", "Tasmania"],
    ["VIC", "Victoria"],
    ["WA", "Western Australia"],
  ],
  CA: [
    ["AB", "Alberta"],
    ["BC", "British Columbia"],
    ["MB", "Manitoba"],
    ["NB", "New Brunswick"],
    ["NL", "Newfoundland and Labrador"],
    ["NS", "Nova Scotia"],
    ["NT", "Northwest Territories"],
    ["NU", "Nunavut"],
    ["ON", "Ontario"],
    ["PE", "Prince Edward Island"],
    ["QC", "Quebec"],
    ["SK", "Saskatchewan"],
    ["YT", "Yukon"],
  ],
  US: [
    ["AL", "Alabama"],
    ["AK", "Alaska"],
    ["AZ", "Arizona"],
    ["AR", "Arkansas"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["DC", "District of Columbia"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["ME", "Maine"],
    ["MD", "Maryland"],
    ["MA", "Massachusetts"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MS", "Mississippi"],
    ["MO", "Missouri"],
    ["MT", "Montana"],
    ["NE", "Nebraska"],
    ["NV", "Nevada"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NY", "New York"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VT", "Vermont"],
    ["VA", "Virginia"],
    ["WA", "Washington"],
    ["WV", "West Virginia"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
    ["AS", "American Samoa"],
    ["GU", "Guam"],
    ["MP", "Northern Mariana Islands"],
    ["PR", "Puerto Rico"],
    ["VI", "U.S. Virgin Islands"],
  ],
}

let countryList

export function countries() {
  if (countryList) return countryList

  const names = new Intl.DisplayNames(["en"], { type: "region" })
  countryList = COUNTRY_CODES.map((code) => ({
    code,
    name: names.of(code) || code,
  }))
    .filter((country) => country.name && country.name !== country.code)
    .sort((a, b) => a.name.localeCompare(b.name, "en"))

  return countryList
}

export function subdivisionsFor(countryCode) {
  const list = SUBDIVISIONS[countryCode] || []
  return list
    .map(([code, name]) => ({
      code: `${countryCode}-${code}`,
      name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "en"))
}

export function needsSubdivision(countryCode) {
  return Boolean(SUBDIVISIONS[countryCode])
}

const COUNTRY_ALIASES = {
  usa: "US",
  "u s": "US",
  "u s a": "US",
  uk: "GB",
  "u k": "GB",
  britain: "GB",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  uae: "AE",
  "u a e": "AE",
  korea: "KR",
  "south korea": "KR",
  "north korea": "KP",
  russia: "RU",
  vietnam: "VN",
  "czech republic": "CZ",
  "ivory coast": "CI",
  "cote divoire": "CI",
  turkey: "TR",
  turkiye: "TR",
  faeroerne: "FO",
  "faroe islands": "FO",
}

const SUBDIVISION_ALIASES = {
  US: {
    "washington dc": "DC",
    "washington d c": "DC",
    "district of columbia": "DC",
    "virgin islands": "VI",
    "us virgin islands": "VI",
  },
}

const nameMaps = new Map()

function fold(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("en")
    .replace(/æ/g, "ae")
    .replace(/ø/g, "o")
    .replace(/å/g, "a")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/['’.]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
}

function namesFor(locale) {
  const key = locale || "en"
  if (nameMaps.has(key)) return nameMaps.get(key)
  let names
  try {
    names = new Intl.DisplayNames([key], { type: "region" })
  } catch {
    nameMaps.set(key, null)
    return null
  }
  const map = new Map()
  for (const code of COUNTRY_CODES) {
    const label = names.of(code)
    if (label) map.set(fold(label), code)
  }
  nameMaps.set(key, map)
  return map
}

export function isCountryCode(code) {
  return COUNTRY_CODES.includes(String(code || "").trim().toUpperCase())
}

export function countryCodeFromName(name, locale = "en") {
  const raw = String(name || "").trim()
  if (!raw) return undefined
  const upper = raw.toUpperCase()
  if (/^[A-Z]{2}$/.test(upper) && COUNTRY_CODES.includes(upper)) return upper
  const folded = fold(raw)
  if (COUNTRY_ALIASES[folded]) return COUNTRY_ALIASES[folded]
  for (const loc of [...new Set([locale, "en"].filter(Boolean))]) {
    const code = namesFor(loc)?.get(folded)
    if (code) return code
  }
  return undefined
}

function subdivisionFromLabel(countryCode, label) {
  const list = subdivisionsFor(countryCode)
  const raw = String(label || "").trim()
  if (!raw) return undefined
  const upper = raw.toUpperCase()
  const direct = list.find(
    (item) => item.code === upper || item.code === `${countryCode}-${upper}`
  )
  if (direct) return direct.code
  const folded = fold(raw)
  const alias = SUBDIVISION_ALIASES[countryCode]?.[folded]
  if (alias) {
    const code = `${countryCode}-${alias}`
    if (list.some((item) => item.code === code)) return code
  }
  return list.find((item) => fold(item.name) === folded)?.code
}

export function subdivisionCode(countryCode, region) {
  const country = String(countryCode || "").trim().toUpperCase()
  if (!needsSubdivision(country)) return undefined
  return subdivisionFromLabel(country, region)
}

function countryFromSubdivisionName(label) {
  for (const country of Object.keys(SUBDIVISIONS)) {
    const folded = fold(label)
    const alias = SUBDIVISION_ALIASES[country]?.[folded]
    if (alias) return { country, region: `${country}-${alias}` }
    const match = subdivisionsFor(country).find((item) => fold(item.name) === folded)
    if (match) return { country, region: match.code }
  }
  return undefined
}

// Dealer labels look like "Denmark, Copenhagen" or "USA, TX, Houston".
export function locationFromDealerLabel(label, locale = "en") {
  const parts = String(label || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  if (!parts.length) return {}

  let country = countryCodeFromName(parts[0], locale)
  let region
  if (!country) {
    const fromState = countryFromSubdivisionName(parts[0])
    if (!fromState) return {}
    country = fromState.country
    region = fromState.region
  }

  if (needsSubdivision(country)) {
    for (const part of parts.slice(1)) {
      const code = subdivisionFromLabel(country, part)
      if (code) {
        region = code
        break
      }
    }
  } else {
    region = undefined
  }

  return region ? { country, region } : { country }
}
